import { ActivatedRoute } from '@angular/router'
import {
  AfterViewInit,
  Injector,
  Component,
  HostListener,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatButtonModule } from '@angular/material/button'
import {
  Product,
  ProductFilter,
  ProductFilterResult,
} from '../../types/products'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator'
import { PaginatorIntl } from '../intl/paginator-intl'
import { FormsModule } from '@angular/forms'
import { RouteService } from '../../services/route.service'
import { ProductService } from '../../services/product.service'
import { ProductListComponent } from '../product-list/product-list.component'
import { ApiError } from '../../types/apiError'
import { DialogService } from '../../services/dialog.service'
import { ErrorService } from '../../services/error.service'
import { SpinnerService } from '../../services/spinner.service'
import { Dialog } from '@angular/cdk/dialog'
import { TranslationService } from '../../services/translation.service'
import { ProductFilterComponent } from '../product-filter/product-filter.component'
import { firstValueFrom } from 'rxjs'

@Component({
  selector: 'app-advanced-search',
  imports: [
    ProductListComponent,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    FormsModule,
    ProductFilterComponent,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: PaginatorIntl }],
  templateUrl: './advanced-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './advanced-search.component.scss',
})
export class AdvancedSearchComponent implements OnInit, AfterViewInit {
  private injector = inject(Injector)
  dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private spinnerService = inject(SpinnerService)
  private routeService = inject(RouteService)
  private productService = inject(ProductService)
  private activatedRoute = inject(ActivatedRoute)
  translationService = inject(TranslationService)

  @Input() isForAddToCatalogue = false
  @Input() shouldShowCopiedIcons = false
  @Output() addToCatalogue: EventEmitter<Product> = new EventEmitter<Product>()
  recordCount: number = 0

  @ViewChild(MatPaginator) paginator!: MatPaginator

  displayedColumns: string[] = ['name', 'brand', 'viewAction']
  popularProducts: Product[] = []

  dataSource = new MatTableDataSource<Product>(this.popularProducts)
  isMobileView = false

  productFilter: ProductFilter = {
    page: 1,
    name: null,
    brand: null,
    sku: null,
    batch: null,
    serial: null,
    gs1Identifier: null,
  }

  async ngOnInit(): Promise<void> {
    this.checkWindowWidth()
    if (this.isForAddToCatalogue) {
      this.displayedColumns = [
        'name',
        'brand',
        'viewAction',
        'addToCatalogueAction',
      ]
    }
    const filter: ProductFilter = {
      name: null,
      brand: null,
      sku: null,
      batch: null,
      serial: null,
      gs1Identifier: null,
    }
    this.activatedRoute.queryParams.subscribe((queryParams) => {
      const prefilledNameFilter = queryParams['name']
      if (prefilledNameFilter != null) {
        filter.name = prefilledNameFilter
        this.filter(filter)
      }
    })
    this.filter(filter)
  }

  /**
   * Handles updating the product list and filtered result count after filtering
   * @param {ProductFilterResult} Filtered product results from the API
   */
  updateProductsList(response: ProductFilterResult): void {
    const products = response.results.map((result: Product) => {
      return {
        id: result.id,
        name: result.name,
        brand: result.brand,
        supplier: result.supplier,
        isCopiedFromSupplier: result.isCopiedFromSupplier,
      }
    })
    this.popularProducts = products
    this.recordCount = response.totalRecords
  }

  ngAfterViewInit(): void {
    // Assigns the paginator to table dataSource
    this.dataSource.paginator = this.paginator
  }

  /**
   * Checks the width of the window
   */
  checkWindowWidth() {
    const windowWidth = window.innerWidth
    this.isMobileView = windowWidth <= 767
  }

  /**
   * Updates the `isMobileView` property used to enable mobile-specific css classes on window resize
   */
  @HostListener('window:resize')
  onWindowResize() {
    this.checkWindowWidth()
  }

  /**
   * Handles the page change events from the paginator and gets the new page data from the API
   * @param {PageEvent} event Event containing new page
   */
  changePage(event: PageEvent) {
    this.productFilter.page = event.pageIndex + 1 // The pageIndex seems to be 0 indexed
    this.queryPage()
  }

  /**
   * Filters products and resets the selected page
   */
  async filter(filter: ProductFilter) {
    try {
      this.spinnerService.startSpinner()
      this.productFilter = {
        ...filter,
        page: 1,
      }
      if (this.isForAddToCatalogue) {
        this.productFilter.isForAddToCatalogue = true
      }
      if (this.paginator != null) {
        //May not be available instantly (eg in ngOnInit), so update only when available
        this.paginator.pageIndex = 0
      }
      const productsResponse = await firstValueFrom(
        this.productService.filterProducts(this.productFilter),
      )
      if (productsResponse != null) {
        this.updateProductsList(productsResponse)
      }

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }
  /**
   * Gets a new page of results
   */
  async queryPage() {
    try {
      this.spinnerService.startSpinner()
      const productsResponse = await firstValueFrom(
        this.productService.filterProducts(this.productFilter),
      )
      if (productsResponse != null) {
        this.updateProductsList(productsResponse)
      }
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }
}
